import { useState, type FormEvent } from 'react';
import { Plus, Pencil, Trash2, Tag, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import FormattedDate from '@/components/common/FormattedDate';
import {
  useWorkflowTypes,
  useCreateWorkflowType,
  useUpdateWorkflowType,
  useDeleteWorkflowType,
} from '@/hooks/useWorkflows';
import type { WorkflowType } from '@/lib/types';

export default function WorkflowTypesTable() {
  const [showCreate, setShowCreate] = useState(false);
  const [editingType, setEditingType] = useState<WorkflowType | null>(null);
  const [deletingName, setDeletingName] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const { data: typesData, isLoading, isError, refetch } = useWorkflowTypes();
  const createType = useCreateWorkflowType();
  const updateType = useUpdateWorkflowType();
  const deleteType = useDeleteWorkflowType();

  const types = typesData?.items || [];

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await createType.mutateAsync({
        name: newName,
        description: newDescription || undefined,
      });
      toast.success('Workflow type created');
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create type');
    }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingType) return;

    try {
      await updateType.mutateAsync({
        name: editingType.name,
        payload: { description: newDescription },
      });
      toast.success('Workflow type updated');
      setEditingType(null);
      setNewDescription('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update type');
    }
  };

  const handleDelete = async (name: string) => {
    if (!confirm(`Delete workflow type "${name}"? This will fail if templates use this type.`)) {
      return;
    }

    setDeletingName(name);
    try {
      await deleteType.mutateAsync(name);
      toast.success('Workflow type deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete type');
    } finally {
      setDeletingName(null);
    }
  };

  const startEdit = (type: WorkflowType) => {
    setShowCreate(false);
    setEditingType(type);
    setNewDescription(type.description || '');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <AlertCircle className="h-12 w-12 mb-4 text-red-400" />
        <p className="text-red-400">Failed to load workflow types</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Create Form */}
      {showCreate ? (
        <form onSubmit={handleCreate} className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
          <h3 className="text-sm font-medium">Create Workflow Type</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="workflow-type-name" className="text-xs font-medium">Name</label>
              <input
                id="workflow-type-name"
                type="text"
                value={newName}
                onChange={(e) =>
                  setNewName(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-+|-+$/g, '')
                  )
                }
                placeholder="workflow-type-name"
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="workflow-type-description" className="text-xs font-medium">Description</label>
              <input
                id="workflow-type-description"
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNewName('');
                setNewDescription('');
              }}
              className="px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createType.isPending}
              className="px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createType.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => {
            setEditingType(null);
            setNewDescription('');
            setShowCreate(true);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm"
        >
          <Plus className="h-4 w-4" />
          Add Workflow Type
        </button>
      )}

      {/* Edit Form */}
      {editingType && (
        <form onSubmit={handleUpdate} className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 space-y-3">
          <h3 className="text-sm font-medium">Edit: {editingType.name}</h3>
          <div>
            <label htmlFor="workflow-type-edit-description" className="text-xs font-medium">Description</label>
            <input
              id="workflow-type-edit-description"
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Description"
              className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditingType(null);
                setNewDescription('');
              }}
              className="px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateType.isPending}
              className="px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-50"
            >
              {updateType.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {/* Types List */}
      {types.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Tag className="h-12 w-12 mb-4 opacity-50" />
          <p>No workflow types defined</p>
          <p className="text-sm mt-1">Create a type to categorize your templates</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Description</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Created</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <tr key={type.name} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm">{type.name}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">{type.description || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <FormattedDate date={type.create_ts} />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(type)}
                        className="p-1.5 rounded-md hover:bg-muted transition-colors"
                        title="Edit"
                        aria-label={`Edit ${type.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(type.name)}
                        disabled={deletingName === type.name}
                        className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete"
                        aria-label={`Delete ${type.name}`}
                      >
                        {deletingName === type.name ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
